/**
 * The Errors tab's tiles (19/10/2026): today's errors (with how many are
 * open), the critical ones, the sends refused, the reads that failed, what
 * Slack heard, and the last error — the Day timeline's tile look.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { instantText } from '../../../utils/clock/dk-label';
import { useCountryClock } from '../../shared/time/country-clock-context';
import type { ErrorFacts } from '../errors-facts';
import { kindWords } from '../errors-words';

const TILE = 'flex min-w-[118px] flex-1 flex-col gap-0.5 rounded-lg border border-border/50 bg-background px-3 py-2 text-left';

const Tile: FC<{ label: string; value: ReactNode; sub?: ReactNode; tone?: 'destructive' | 'warning' | 'success' }> = ({ label, value, sub, tone }) => (
  <div className={cn(TILE, tone === 'destructive' && 'border-destructive/40 bg-destructive/5', tone === 'warning' && 'border-warning/40 bg-warning/5')}>
    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className={cn('font-mono text-base font-semibold tabular-nums', tone === 'destructive' && 'text-destructive', tone === 'success' && 'text-success')}>{value}</span>
    {sub ? <span className="truncate text-[11px] text-muted-foreground">{sub}</span> : null}
  </div>
);

export const ErrorTiles: FC<{ facts: ErrorFacts }> = ({ facts: f }) => {
  const clock = useCountryClock();
  const slackTone = f.slack.failed > 0 ? 'destructive' : f.slack.due > 0 && f.slack.sent === f.slack.due ? 'success' : undefined;
  return (
    <div className="flex flex-wrap gap-2 px-5 py-3" data-testid="error-tiles">
      <Tile label="Today" value={f.today} sub={f.today === 0 ? 'no error' : [`${f.open} open`, f.resolved > 0 ? `${f.resolved} resolved` : null, f.happened > f.today ? `happened ×${f.happened}` : null].filter(Boolean).join(' · ')} tone={f.open > 0 ? 'warning' : undefined} />
      <Tile label="Critical" value={f.critical} sub={f.critical > 0 ? 'sends stop or a person must act' : 'nothing stopped'} tone={f.critical > 0 ? 'destructive' : undefined} />
      <Tile label="Sends refused" value={f.sends} sub={f.sends > 0 ? 'retried each minute, halted at the limit' : 'every send taken'} />
      <Tile label="Reads failed" value={f.reads} sub={f.reads > 0 ? 'judged with less' : 'tracker and stats read'} />
      <Tile label="Slack" value={f.slack.due > 0 ? `${f.slack.sent} / ${f.slack.due}` : '—'} sub={f.slack.failed > 0 ? `${f.slack.failed} not delivered` : f.slack.due > 0 ? 'alerts delivered today' : 'nothing to alert today'} tone={slackTone} />
      <Tile label="Last error" value={f.last ? instantText(f.last.at, clock) : '—'} sub={f.last ? kindWords(f.last.kind).label : 'none'} />
    </div>
  );
};
